import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiFetch } from './config';
import Nav from './Nav';
import { computeLayout, computeTrustLayout, trustToY } from './forceLayout';
import {
  TrustBar,
  LensChips,
  fmtScore,
  scoreColor,
  scoreScale,
  relationColor,
} from './trustShared';
import { Explain } from './Explain';
import { useLensWeights, recombine } from './lensWeights';
import './css/trust.css';

const GRAPH_WIDTH = 920;
const GRAPH_HEIGHT = 480;
// MIRROR of backend/trust/axes.ts (vector-trust phase 1, DECISIONS.md
// 2026-07-26): the frontend cannot import backend TS, and this page is
// FROZEN by decision — consolidate onto the phase-2 axis metadata API only
// if this set ever needs to change.
const EPISTEMIC = new Set(['supports', 'contradicts']);

const snip = (s, n = 60) => (s.length > n ? `${s.substring(0, n)}…` : s);
const nodeRadius = (n) => 9 + 7 * ((n.combined + 1) / 2);

// Connected components over supports/contradicts edges = emergent "threads"
// of argument. Components of one node are just unengaged claims, skip them.
function computeThreads(nodes, edges) {
  const epistemicEdges = edges.filter((e) => EPISTEMIC.has(e.relation_name));
  const adjacency = {};
  for (const e of epistemicEdges) {
    (adjacency[e.source] = adjacency[e.source] || []).push(e.target);
    (adjacency[e.target] = adjacency[e.target] || []).push(e.source);
  }
  const seen = new Set();
  const threads = [];
  for (const node of nodes) {
    if (seen.has(node.id) || !adjacency[node.id]) continue;
    const component = new Set();
    const stack = [node.id];
    while (stack.length) {
      const id = stack.pop();
      if (component.has(id)) continue;
      component.add(id);
      seen.add(id);
      for (const next of adjacency[id] || []) stack.push(next);
    }
    if (component.size >= 2) {
      threads.push({
        nodeIds: component,
        edges: epistemicEdges.filter((e) => component.has(e.source) && component.has(e.target)),
      });
    }
  }
  return threads.sort((a, b) => b.edges.length - a.edges.length);
}

// Quadratic-curve geometry for an edge: endpoints pulled back to the node
// borders so arrowheads land on the circle, bowed perpendicular to the line
// so opposite-direction edge pairs don't overlap.
function edgeGeometry(e, positions, radiusById) {
  const S = positions[e.source];
  const T = positions[e.target];
  if (!S || !T) return null;
  const dx = T.x - S.x;
  const dy = T.y - S.y;
  const dist = Math.sqrt(dx * dx + dy * dy) || 0.01;
  const sign = e.source < e.target ? 1 : -1;
  const bow = Math.min(40, 12 + dist * 0.08) * sign;
  const C = { x: (S.x + T.x) / 2 + (-dy / dist) * bow, y: (S.y + T.y) / 2 + (dx / dist) * bow };

  const pullToward = (from, toward, by) => {
    const ddx = toward.x - from.x;
    const ddy = toward.y - from.y;
    const d = Math.sqrt(ddx * ddx + ddy * ddy) || 0.01;
    return { x: from.x + (ddx / d) * by, y: from.y + (ddy / d) * by };
  };
  const start = pullToward(S, C, (radiusById[e.source] || 10) + 2);
  const end = pullToward(T, C, (radiusById[e.target] || 10) + 9);
  const mid = {
    x: 0.25 * start.x + 0.5 * C.x + 0.25 * end.x,
    y: 0.25 * start.y + 0.5 * C.y + 0.25 * end.y,
  };
  return { start, control: C, end, mid };
}

function DomainGraph({ nodes, edges, selectedId, onSelect, layoutMode }) {
  const svgRef = useRef(null);
  const dragRef = useRef(null);
  const [positions, setPositions] = useState({});
  const [hoveredId, setHoveredId] = useState(null);

  const layout = useMemo(
    () =>
      layoutMode === 'organized'
        ? computeTrustLayout(nodes, edges, GRAPH_WIDTH, GRAPH_HEIGHT)
        : computeLayout(nodes.map((n) => n.id), edges, GRAPH_WIDTH, GRAPH_HEIGHT),
    [nodes, edges, layoutMode]
  );
  useEffect(() => setPositions(layout), [layout]);

  const radiusById = useMemo(() => {
    const map = {};
    for (const n of nodes) map[n.id] = nodeRadius(n);
    return map;
  }, [nodes]);

  const neighbors = useMemo(() => {
    const map = {};
    for (const n of nodes) map[n.id] = new Set([n.id]);
    for (const e of edges) {
      map[e.source]?.add(e.target);
      map[e.target]?.add(e.source);
    }
    return map;
  }, [nodes, edges]);

  const relationNames = [...new Set(edges.map((e) => e.relation_name))];
  const focusId = hoveredId || selectedId;
  const nodeFaded = (id) => focusId && !neighbors[focusId]?.has(id);
  const edgeFaded = (e) => focusId && e.source !== focusId && e.target !== focusId;

  const toSvgCoords = (evt) => {
    const rect = svgRef.current.getBoundingClientRect();
    return {
      x: ((evt.clientX - rect.left) * GRAPH_WIDTH) / rect.width,
      y: ((evt.clientY - rect.top) * GRAPH_HEIGHT) / rect.height,
    };
  };

  const handlePointerDown = (evt, id) => {
    evt.target.setPointerCapture(evt.pointerId);
    dragRef.current = { id, start: toSvgCoords(evt), moved: false };
  };
  const handlePointerMove = (evt) => {
    const drag = dragRef.current;
    if (!drag) return;
    const p = toSvgCoords(evt);
    if (!drag.moved) {
      const d = Math.hypot(p.x - drag.start.x, p.y - drag.start.y);
      if (d < 4) return;
      drag.moved = true;
    }
    setPositions((prev) => ({
      ...prev,
      [drag.id]: {
        x: Math.max(20, Math.min(GRAPH_WIDTH - 20, p.x)),
        y: Math.max(20, Math.min(GRAPH_HEIGHT - 20, p.y)),
      },
    }));
  };
  const handlePointerUp = (evt) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (drag && !drag.moved) onSelect(drag.id === selectedId ? null : drag.id);
  };

  return (
    <div className="trust-graph-wrap">
      <svg
        ref={svgRef}
        width="100%"
        viewBox={`0 0 ${GRAPH_WIDTH} ${GRAPH_HEIGHT}`}
        style={{ display: 'block' }}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onClick={(e) => { if (e.target === svgRef.current) onSelect(null); }}
      >
        {layoutMode === 'organized' && (
          <g style={{ pointerEvents: 'none' }}>
            {[1, 0.5, 0, -0.5, -1].map((s) => {
              const y = trustToY(s, GRAPH_HEIGHT);
              return (
                <g key={s}>
                  <line
                    x1={34}
                    x2={GRAPH_WIDTH - 12}
                    y1={y}
                    y2={y}
                    stroke={s === 0 ? 'var(--wv-border)' : 'var(--wv-border-strong)'}
                    strokeDasharray={s === 0 ? undefined : '3 5'}
                  />
                  <text x={8} y={y + 3.5} fontSize="10" fill="var(--wv-muted)">
                    {s > 0 ? `+${s}` : s}
                  </text>
                </g>
              );
            })}
          </g>
        )}

        <defs>
          {relationNames.map((name) => (
            <marker
              key={name}
              id={`arrow-${name}`}
              viewBox="0 0 10 10"
              refX="8"
              refY="5"
              markerWidth="7"
              markerHeight="7"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill={relationColor(name)} />
            </marker>
          ))}
        </defs>

        {edges.map((e) => {
          const geo = edgeGeometry(e, positions, radiusById);
          if (!geo) return null;
          const voted = e.up_votes + e.down_votes > 0;
          return (
            <g key={e.id} style={{ opacity: edgeFaded(e) ? 0.12 : 1, transition: 'opacity 0.15s' }}>
              <path
                d={`M ${geo.start.x} ${geo.start.y} Q ${geo.control.x} ${geo.control.y} ${geo.end.x} ${geo.end.y}`}
                fill="none"
                stroke={relationColor(e.relation_name)}
                strokeWidth={1.5 + Math.min(3, Math.abs(e.vote_score) * 0.6)}
                strokeDasharray={e.polarity === -1 ? '6 4' : undefined}
                strokeLinecap="round"
                markerEnd={`url(#arrow-${e.relation_name})`}
                opacity={0.85}
              >
                <title>
                  {`${e.relation_name}${e.polarity === -1 ? ' (negative)' : ''} · ${e.up_votes}↑ ${e.down_votes}↓${voted ? '' : ' · unvoted (inert)'}`}
                </title>
              </path>
              {voted && (
                <g transform={`translate(${geo.mid.x}, ${geo.mid.y})`}>
                  <rect x={-14} y={-9} width={28} height={17} rx={8.5} fill="var(--wv-surface)" stroke="var(--wv-border-strong)" />
                  <text
                    y={3.5}
                    textAnchor="middle"
                    fontSize="10"
                    fontWeight="600"
                    fill={scoreColor(e.vote_score)}
                  >
                    {e.vote_score > 0 ? `+${e.vote_score}` : e.vote_score === 0 ? '±0' : e.vote_score}
                  </text>
                </g>
              )}
            </g>
          );
        })}

        {nodes.map((n) => {
          const p = positions[n.id];
          if (!p) return null;
          const r = radiusById[n.id];
          const selected = n.id === selectedId;
          return (
            <g
              key={n.id}
              className="trust-graph-node"
              style={{ opacity: nodeFaded(n.id) ? 0.15 : 1, transition: 'opacity 0.15s' }}
              onPointerDown={(evt) => handlePointerDown(evt, n.id)}
              onMouseEnter={() => setHoveredId(n.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              {selected && (
                <circle cx={p.x} cy={p.y} r={r + 5} fill="none" stroke="var(--wv-text)" strokeWidth={2} strokeDasharray="3 3" />
              )}
              <circle
                cx={p.x}
                cy={p.y}
                r={r}
                fill={scoreScale(n.combined)}
                stroke="var(--wv-surface)"
                strokeWidth={2}
              >
                <title>{`${n.content}\ncombined ${fmtScore(n.combined)}`}</title>
              </circle>
              {n.conflict && (
                <text x={p.x + r - 1} y={p.y - r + 3} fontSize="13" fill="var(--wv-warn)" style={{ pointerEvents: 'none' }}>
                  ⚡
                </text>
              )}
              <text
                x={Math.max(88, Math.min(GRAPH_WIDTH - 88, p.x))}
                y={p.y + r + 14}
                fontSize="10.5"
                fill="var(--wv-text)"
                textAnchor="middle"
                style={{ paintOrder: 'stroke', stroke: 'var(--wv-bg)', strokeWidth: 3.5, pointerEvents: 'none' }}
              >
                {snip(n.content, 28)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function GraphLegend({ edges, layoutMode }) {
  const used = [...new Set(edges.map((e) => e.relation_name))];
  return (
    <div
      className="trust-muted"
      style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', fontSize: '12px', marginTop: '10px', alignItems: 'center' }}
    >
      {used.map((name) => (
        <span key={name} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ width: '18px', height: '3px', borderRadius: '2px', backgroundColor: relationColor(name), display: 'inline-block' }} />
          {name}
        </span>
      ))}
      <span>
        <Explain concept="graph-legend">
          {layoutMode === 'organized' ? 'height = combined trust · bands = threads · ' : ''}
          node size & color = combined trust · dashed = negative polarity · pill = net votes · ⚡ = contested · drag to rearrange, click to inspect
        </Explain>
      </span>
    </div>
  );
}

// Above this many nodes, the graph shows only the most relevant by default.
const TOP_N = 30;

function TrustDomain() {
  const { tagId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  // Graph view controls. Filters are reading lenses over the domain (see
  // DECISIONS.md: rigidity lives in how the graph is read, never stored).
  const [layoutMode, setLayoutMode] = useState('organized');
  const [showAll, setShowAll] = useState(false);
  const [minTrust, setMinTrust] = useState(-1);
  const [hiddenRelations, setHiddenRelations] = useState(() => new Set());
  const [hideInert, setHideInert] = useState(false);
  const [focusDepth, setFocusDepth] = useState(0);

  useEffect(() => {
    const fetchDomain = async () => {
      try {
        setLoading(true);
        const response = await apiFetch(`/api/trust/domains/${tagId}`);
        if (!response.ok) throw new Error('Domain not found');
        setData(await response.json());
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching domain:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDomain();
    setSelectedId(null);
  }, [tagId]);

  // Combined scores recombined under the viewer's lens weights (all-1 default
  // reproduces the backend's mean exactly). Everything downstream — colors,
  // sizes, ranking, filters — follows the viewer's blend.
  const { weights } = useLensWeights();
  const weightedNodes = useMemo(
    () => (data?.nodes || []).map((n) => ({ ...n, combined: recombine(n.scores, weights) })),
    [data, weights]
  );

  const nodesById = useMemo(() => {
    const map = {};
    for (const n of weightedNodes) map[n.id] = n;
    return map;
  }, [weightedNodes]);

  const threads = useMemo(() => computeThreads(data?.nodes || [], data?.edges || []), [data]);

  // The filtered slice of the domain the graph renders. Pipeline: edge filters
  // (relation type, inert) → node trust cutoff → focus neighborhood OR top-N
  // by relevance (degree + trust strength). The selected node always survives.
  const graphView = useMemo(() => {
    if (!data) return { nodes: [], edges: [], truncated: false };
    const edgesFiltered = data.edges.filter(
      (e) =>
        !hiddenRelations.has(e.relation_name) &&
        (!hideInert || e.up_votes + e.down_votes > 0)
    );
    let nodes = weightedNodes.filter((n) => n.combined >= minTrust || n.id === selectedId);
    let nodeIds = new Set(nodes.map((n) => n.id));
    const edges = edgesFiltered.filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target));

    let truncated = false;
    if (focusDepth > 0 && selectedId && nodeIds.has(selectedId)) {
      const adjacency = new Map();
      for (const e of edges) {
        if (!adjacency.has(e.source)) adjacency.set(e.source, []);
        if (!adjacency.has(e.target)) adjacency.set(e.target, []);
        adjacency.get(e.source).push(e.target);
        adjacency.get(e.target).push(e.source);
      }
      const keep = new Set([selectedId]);
      let frontier = [selectedId];
      for (let depth = 0; depth < focusDepth; depth++) {
        const next = [];
        for (const id of frontier) {
          for (const neighbor of adjacency.get(id) || []) {
            if (!keep.has(neighbor)) {
              keep.add(neighbor);
              next.push(neighbor);
            }
          }
        }
        frontier = next;
      }
      nodes = nodes.filter((n) => keep.has(n.id));
    } else if (!showAll && nodes.length > TOP_N) {
      const degree = {};
      for (const e of edges) {
        degree[e.source] = (degree[e.source] || 0) + 1;
        degree[e.target] = (degree[e.target] || 0) + 1;
      }
      const relevance = (n) => (degree[n.id] || 0) + 3 * Math.abs(n.combined);
      const kept = [...nodes]
        .sort((a, b) => relevance(b) - relevance(a) || (a.id < b.id ? -1 : 1))
        .slice(0, TOP_N);
      if (selectedId && nodeIds.has(selectedId) && !kept.some((n) => n.id === selectedId)) {
        kept.push(nodes.find((n) => n.id === selectedId));
      }
      nodes = kept;
      truncated = true;
    }

    nodeIds = new Set(nodes.map((n) => n.id));
    return {
      nodes,
      edges: edges.filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target)),
      truncated,
    };
  }, [data, weightedNodes, hiddenRelations, hideInert, minTrust, selectedId, focusDepth, showAll]);

  const toggleRelation = (name) =>
    setHiddenRelations((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  if (loading) return <div style={{ padding: '20px' }}>Loading...</div>;
  if (error) return <div style={{ padding: '20px', color: 'var(--wv-neg)' }}>Error: {error}</div>;
  if (!data) return null;

  const { tag, lenses, edges } = data;
  const nodes = weightedNodes;
  const combined = nodes.length
    ? nodes.reduce((sum, n) => sum + n.combined, 0) / nodes.length
    : 0;
  const ranked = [...nodes].sort((a, b) => b.combined - a.combined);
  const selected = selectedId ? nodesById[selectedId] : null;

  return (
    <div className="trust-page" style={{ maxWidth: '980px', margin: '0 auto' }}>
      <Nav />
      <div style={{ padding: '0 20px 40px' }}>
        <Link to="/trust" style={{ marginBottom: '16px', display: 'inline-block', color: 'var(--wv-accent)' }}>
          &larr; Back to atlas
        </Link>

        <div style={{ display: 'flex', alignItems: 'baseline', gap: '14px', flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0 }}>
            <Explain concept="domain">{tag.name}</Explain>
          </h1>
          {tag.parent_name && <span className="trust-muted">⊂ {tag.parent_name}</span>}
          <Explain concept="combined-score">
            <strong style={{ color: scoreColor(combined), fontSize: '20px' }}>{fmtScore(combined)}</strong>
          </Explain>
          <TrustBar score={combined} width={180} height={10} />
        </div>
        <div style={{ margin: '12px 0 20px' }}>
          <LensChips
            lenses={lenses}
            scores={Object.fromEntries(
              lenses.map((l) => [
                l.id,
                nodes.length ? nodes.reduce((s, n) => s + (n.scores[l.id] || 0), 0) / nodes.length : 0,
              ])
            )}
          />
        </div>

        {nodes.length === 0 ? (
          <p className="trust-muted">No objects in this domain.</p>
        ) : (
          <>
            <div className="graph-filters">
              <Explain concept="graph-filters">
                <strong style={{ fontSize: '12px', color: 'var(--wv-muted)' }}>View</strong>
              </Explain>
              <label>
                <Explain concept="organized-layout">layout</Explain>{' '}
                <select value={layoutMode} onChange={(e) => setLayoutMode(e.target.value)}>
                  <option value="organized">organized (trust × thread)</option>
                  <option value="free">free (force)</option>
                </select>
              </label>
              <label>
                min trust <strong>{fmtScore(minTrust)}</strong>
                <input
                  type="range"
                  min={-1}
                  max={1}
                  step={0.05}
                  value={minTrust}
                  onChange={(e) => setMinTrust(parseFloat(e.target.value))}
                  style={{ width: '90px', verticalAlign: 'middle', marginLeft: '6px' }}
                />
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={hideInert}
                  onChange={(e) => setHideInert(e.target.checked)}
                />{' '}
                hide unvoted edges
              </label>
              <label>
                <Explain concept="focus">focus</Explain>{' '}
                <select
                  value={focusDepth}
                  disabled={!selectedId}
                  title={selectedId ? '' : 'Select a node first'}
                  onChange={(e) => setFocusDepth(parseInt(e.target.value, 10))}
                >
                  <option value={0}>off</option>
                  <option value={1}>1 hop</option>
                  <option value={2}>2 hops</option>
                </select>
              </label>
              <span className="graph-filter-relations">
                {[...new Set(edges.map((e) => e.relation_name))].sort().map((name) => (
                  <label key={name} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <input
                      type="checkbox"
                      checked={!hiddenRelations.has(name)}
                      onChange={() => toggleRelation(name)}
                    />
                    <span
                      style={{
                        width: '10px',
                        height: '3px',
                        borderRadius: '2px',
                        backgroundColor: relationColor(name),
                        display: 'inline-block',
                      }}
                    />
                    {name}
                  </label>
                ))}
              </span>
            </div>

            <div className="trust-muted" style={{ fontSize: '12px', margin: '0 0 8px' }}>
              showing {graphView.nodes.length} of {nodes.length} objects · {graphView.edges.length} of{' '}
              {edges.length} connections
              {graphView.truncated && (
                <>
                  {' '}
                  (top {TOP_N} by relevance —{' '}
                  <span
                    className="thread-link"
                    onClick={() => setShowAll(true)}
                    style={{ color: 'var(--wv-accent)' }}
                  >
                    show all
                  </span>
                  )
                </>
              )}
              {showAll && nodes.length > TOP_N && (
                <>
                  {' '}
                  (
                  <span
                    className="thread-link"
                    onClick={() => setShowAll(false)}
                    style={{ color: 'var(--wv-accent)' }}
                  >
                    show top {TOP_N}
                  </span>
                  )
                </>
              )}
            </div>

            <DomainGraph
              nodes={graphView.nodes}
              edges={graphView.edges}
              selectedId={selectedId}
              onSelect={setSelectedId}
              layoutMode={layoutMode}
            />
            <GraphLegend edges={graphView.edges} layoutMode={layoutMode} />

            {selected && (
              <div className="trust-section" style={{ marginTop: '16px', borderColor: 'var(--wv-border-strong)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ whiteSpace: 'pre-wrap', fontWeight: 500 }}>{selected.content}</div>
                  <span style={{ flexShrink: 0, fontSize: '13px', display: 'flex', gap: '12px' }}>
                    <Link to={`/object/${selected.id}`} style={{ color: 'var(--wv-accent)' }}>object →</Link>
                  </span>
                </div>
                <div className="trust-muted" style={{ fontSize: '12px', margin: '8px 0' }}>
                  by {selected.creator_username || 'unknown'}
                  {selected.conflict && <span style={{ color: 'var(--wv-warn)', marginLeft: '8px' }}>⚡ contested</span>}
                </div>
                <LensChips lenses={lenses} scores={selected.scores} size="small" />
              </div>
            )}

            <h2 style={{ marginTop: '34px' }}>
              <Explain concept="threads">Threads</Explain>
            </h2>
            {threads.length === 0 ? (
              <p className="trust-muted">No supports/contradicts chains in this domain yet.</p>
            ) : (
              threads.map((thread, i) => (
                <div key={i} className="thread-box">
                  {thread.edges.map((e) => (
                    <div key={e.id} style={{ fontSize: '13px', marginBottom: '6px', lineHeight: 1.7 }}>
                      <span className="thread-link" onClick={() => setSelectedId(e.source)}>
                        {snip(nodesById[e.source]?.content || '?', 50)}
                      </span>
                      <span className="relation-pill" style={{ backgroundColor: relationColor(e.relation_name) }}>
                        {e.relation_name}
                        {e.polarity === -1 ? ' (neg)' : ''} →
                      </span>
                      <span className="thread-link" onClick={() => setSelectedId(e.target)}>
                        {snip(nodesById[e.target]?.content || '?', 50)}
                      </span>
                      {e.up_votes + e.down_votes > 0 && (
                        <span className="vote-tag">
                          {e.vote_score > 0 ? `+${e.vote_score}` : e.vote_score === 0 ? '±0' : e.vote_score}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ))
            )}

            <h2 style={{ marginTop: '34px' }}>
              <Explain concept="trust-ranking">Objects by trust ({ranked.length})</Explain>
            </h2>
            <div>
              {ranked.map((n) => (
                <div
                  key={n.id}
                  className={`trust-row${n.id === selectedId ? ' selected' : ''}`}
                  onClick={() => setSelectedId(n.id === selectedId ? null : n.id)}
                >
                  <strong style={{ color: scoreColor(n.combined), minWidth: '52px', textAlign: 'right' }}>
                    {fmtScore(n.combined)}
                  </strong>
                  <div style={{ flex: 1 }}>
                    <div>
                      {n.content}
                      {n.conflict && <span style={{ color: 'var(--wv-warn)', marginLeft: '8px' }}>⚡</span>}
                    </div>
                    <div style={{ marginTop: '7px' }}>
                      <LensChips lenses={lenses} scores={n.scores} size="small" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default TrustDomain;
